import { localizePath as __lp } from "@/i18n/locales";
import SEOHead from "@/components/SEOHead";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLocale } from "@/i18n/LocaleProvider";
import { LANG_HREFLANG, type Lang } from "@/i18n/locales";

type Block =
  | { type: "p"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] };

interface Section {
  heading: string;
  blocks: Block[];
}

interface PrivacyContent {
  seoTitle: string;
  seoDesc: string;
  backToHome: string;
  title: string;
  lastUpdated: string;
  breadcrumbHome: string;
  breadcrumbSelf: string;
  controllerHeading: string;
  emailLabel: string;
  rightsContact: string;
  sections: Section[];
}

const EMAIL = "mail@devcore.systems";
const ADDRESS = [
  "DevCore Websystems Ltd.",
  "Tremithias, 1, Salacia Beach Residence Flat/Office 41",
  "8310 Paphos, Cyprus",
];

const CONTENT: Record<Lang, PrivacyContent> = {
  en: {
    seoTitle: "Privacy Policy | DevCore Websystems Ltd.",
    seoDesc:
      "How DevCore Websystems Ltd. handles your personal data — including contact forms, advertising, lead generation and LinkedIn Lead Gen Forms — under the GDPR.",
    backToHome: "Back to Home",
    title: "Privacy Policy",
    lastUpdated: "Last updated",
    breadcrumbHome: "Home",
    breadcrumbSelf: "Privacy Policy",
    controllerHeading: "2. Controller",
    emailLabel: "Email",
    rightsContact: "To exercise any of these rights, please contact us at",
    sections: [
      {
        heading: "1. Overview",
        blocks: [
          {
            type: "p",
            text: 'DevCore Websystems Ltd. ("we", "us", "our") takes the protection of your personal data very seriously. This privacy policy explains what data we collect when you visit our website or contact us, how we use it, and what rights you have under the EU General Data Protection Regulation (GDPR).',
          },
        ],
      },
      {
        heading: "3. Data Collection on Our Website",
        blocks: [
          { type: "h3", text: "Server Log Files" },
          {
            type: "p",
            text: "Our hosting provider automatically collects and stores information in server log files that your browser transmits to us. These include: browser type and version, operating system, referrer URL, hostname of the accessing computer, and time of the server request. This data cannot be attributed to specific individuals and is not combined with other data sources. The legal basis is our legitimate interest in the secure and stable operation of our website (Art. 6(1)(f) GDPR).",
          },
          { type: "h3", text: "Contact & Booking Forms" },
          {
            type: "p",
            text: "If you contact us via our contact form or booking form, the data you provide (e.g. name, email, your message, and the meeting details you choose) is stored to process your request and for any follow-up questions. The legal basis is the performance of pre-contractual measures and our legitimate interest in responding (Art. 6(1)(b) and (f) GDPR); where the form is also used to contact you for advertising purposes, processing is based on your consent (Art. 6(1)(a) GDPR), see section 6.",
          },
        ],
      },
      {
        heading: "4. Cookies",
        blocks: [
          {
            type: "p",
            text: "Our website uses cookies. Cookies are small text files stored on your device by your browser; they cause no damage. Strictly necessary cookies are required for the website to function and are set on the basis of our legitimate interest (Art. 6(1)(f) GDPR). Analytics and marketing cookies are only set after you give your consent via our cookie banner (Art. 6(1)(a) GDPR). You can change or withdraw your choice at any time via the cookie settings.",
          },
        ],
      },
      {
        heading: "5. Analytics & Advertising Technologies",
        blocks: [
          {
            type: "p",
            text: "Subject to your consent, we use analytics and advertising services such as Google, Meta (Facebook/Instagram) and LinkedIn. These services may set cookies or similar technologies and process information about your use of the website (e.g. IP address, browser type, pages visited, conversions) to help us understand our audience and measure the performance of our advertising. Data may be transferred to third countries (e.g. the USA) on the basis of EU Standard Contractual Clauses. The legal basis is your consent (Art. 6(1)(a) GDPR), which you can withdraw at any time.",
          },
        ],
      },
      {
        heading: "6. Direct Marketing, Lead Generation & LinkedIn Lead Gen",
        blocks: [
          {
            type: "p",
            text: "We run advertising and lead-generation campaigns (e.g. via Google, Meta and LinkedIn, including LinkedIn Lead Gen Forms) to find new clients. If you submit one of our lead, contact or booking forms — or a LinkedIn Lead Gen Form within the LinkedIn platform — we process the details you provide in order to contact you about a possible collaboration, including by email, phone or via LinkedIn.",
          },
          {
            type: "p",
            text: "Legal basis: We contact you for these advertising purposes only on the basis of your explicit consent under Art. 6(1)(a) GDPR, which you actively give via the checkbox on our forms or by submitting a LinkedIn Lead Gen Form.",
          },
          { type: "h3", text: "LinkedIn Lead Gen Forms" },
          {
            type: "p",
            text: "When you submit a LinkedIn Lead Gen Form, the data you have stored in your LinkedIn profile (such as name, email and company) is transmitted from LinkedIn to us so that we can follow up. For the delivery of the advertising and the operation of the form, LinkedIn Ireland Unlimited Company acts as an independent controller; please also refer to LinkedIn's own privacy policy. From the moment your data reaches us, we are the controller responsible for the processing described in this policy.",
          },
          {
            type: "p",
            text: "We document the consent you give (including the time it was given) in order to demonstrate compliance. We retain lead data only for as long as necessary to pursue the potential collaboration, after which it is deleted unless a contractual relationship is established or statutory retention periods apply.",
          },
          { type: "h3", text: "Your right to withdraw" },
          {
            type: "p",
            text: `You can withdraw your consent to be contacted for advertising purposes at any time, with effect for the future and without giving reasons, by emailing ${EMAIL}. Withdrawing your consent does not affect the lawfulness of processing carried out before the withdrawal.`,
          },
        ],
      },
      {
        heading: "7. Your Rights",
        blocks: [
          { type: "p", text: "You have the following rights regarding your personal data:" },
          {
            type: "ul",
            items: [
              "Right to access your stored personal data",
              "Right to rectification of inaccurate data",
              "Right to erasure of your data",
              "Right to restriction of processing",
              "Right to data portability",
              "Right to object to processing",
              "Right to withdraw consent at any time",
              "Right to lodge a complaint with a supervisory authority (e.g. the Office of the Commissioner for Personal Data Protection of Cyprus)",
            ],
          },
        ],
      },
      {
        heading: "8. Data Security",
        blocks: [
          {
            type: "p",
            text: "We use industry-standard security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Our website uses SSL/TLS encryption for secure data transmission.",
          },
        ],
      },
      {
        heading: "9. Changes to This Policy",
        blocks: [
          {
            type: "p",
            text: "We reserve the right to update this privacy policy to reflect changes in our practices or applicable laws. The current version is always available on our website.",
          },
        ],
      },
    ],
  },
  de: {
    seoTitle: "Datenschutzerklärung | DevCore Websystems Ltd.",
    seoDesc:
      "Wie die DevCore Websystems Ltd. mit deinen personenbezogenen Daten umgeht – inklusive Kontaktformularen, Werbung, Lead-Generierung und LinkedIn Lead Gen Forms – nach der DSGVO.",
    backToHome: "Zurück zur Startseite",
    title: "Datenschutzerklärung",
    lastUpdated: "Zuletzt aktualisiert",
    breadcrumbHome: "Startseite",
    breadcrumbSelf: "Datenschutzerklärung",
    controllerHeading: "2. Verantwortlicher",
    emailLabel: "E-Mail",
    rightsContact: "Um eines dieser Rechte auszuüben, kontaktiere uns bitte unter",
    sections: [
      {
        heading: "1. Überblick",
        blocks: [
          {
            type: "p",
            text: 'Die DevCore Websystems Ltd. („wir", „uns") nimmt den Schutz deiner personenbezogenen Daten sehr ernst. Diese Datenschutzerklärung erklärt, welche Daten wir erheben, wenn du unsere Website besuchst oder uns kontaktierst, wie wir sie nutzen und welche Rechte du nach der EU-Datenschutz-Grundverordnung (DSGVO) hast.',
          },
        ],
      },
      {
        heading: "3. Datenerfassung auf unserer Website",
        blocks: [
          { type: "h3", text: "Server-Logfiles" },
          {
            type: "p",
            text: "Unser Hosting-Anbieter erhebt und speichert automatisch Informationen in Server-Logfiles, die dein Browser an uns übermittelt. Dazu gehören: Browsertyp und -version, Betriebssystem, Referrer-URL, Hostname des zugreifenden Rechners und Uhrzeit der Serveranfrage. Diese Daten lassen sich keiner bestimmten Person zuordnen und werden nicht mit anderen Datenquellen zusammengeführt. Rechtsgrundlage ist unser berechtigtes Interesse am sicheren und stabilen Betrieb unserer Website (Art. 6 Abs. 1 lit. f DSGVO).",
          },
          { type: "h3", text: "Kontakt- & Buchungsformulare" },
          {
            type: "p",
            text: "Wenn du uns über das Kontaktformular oder das Buchungsformular kontaktierst, speichern wir die von dir angegebenen Daten (z. B. Name, E-Mail, deine Nachricht und die gewählten Termin-Details), um deine Anfrage zu bearbeiten und für etwaige Anschlussfragen. Rechtsgrundlage ist die Durchführung vorvertraglicher Maßnahmen sowie unser berechtigtes Interesse an der Beantwortung (Art. 6 Abs. 1 lit. b und f DSGVO); soweit das Formular auch dazu dient, dich zu Werbezwecken zu kontaktieren, erfolgt die Verarbeitung auf Grundlage deiner Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), siehe Abschnitt 6.",
          },
        ],
      },
      {
        heading: "4. Cookies",
        blocks: [
          {
            type: "p",
            text: "Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die dein Browser auf deinem Gerät speichert; sie richten keinen Schaden an. Technisch notwendige Cookies sind für den Betrieb der Website erforderlich und werden auf Grundlage unseres berechtigten Interesses gesetzt (Art. 6 Abs. 1 lit. f DSGVO). Analyse- und Marketing-Cookies setzen wir erst, nachdem du über unseren Cookie-Banner eingewilligt hast (Art. 6 Abs. 1 lit. a DSGVO). Du kannst deine Auswahl jederzeit über die Cookie-Einstellungen ändern oder widerrufen.",
          },
        ],
      },
      {
        heading: "5. Analyse & Werbe-Technologien",
        blocks: [
          {
            type: "p",
            text: "Vorbehaltlich deiner Einwilligung nutzen wir Analyse- und Werbedienste wie Google, Meta (Facebook/Instagram) und LinkedIn. Diese Dienste können Cookies oder ähnliche Technologien setzen und Informationen über deine Nutzung der Website verarbeiten (z. B. IP-Adresse, Browsertyp, besuchte Seiten, Conversions), damit wir unsere Zielgruppe verstehen und die Leistung unserer Werbung messen können. Daten können auf Grundlage der EU-Standardvertragsklauseln in Drittländer (z. B. die USA) übermittelt werden. Rechtsgrundlage ist deine Einwilligung (Art. 6 Abs. 1 lit. a DSGVO), die du jederzeit widerrufen kannst.",
          },
        ],
      },
      {
        heading: "6. Direktwerbung, Lead-Generierung & LinkedIn Lead Gen",
        blocks: [
          {
            type: "p",
            text: "Wir schalten Werbe- und Lead-Gen-Kampagnen (z. B. über Google, Meta und LinkedIn, einschließlich LinkedIn Lead Gen Forms), um neue Kund:innen zu gewinnen. Wenn du eines unserer Lead-, Kontakt- oder Buchungsformulare absendest – oder ein LinkedIn Lead Gen Form innerhalb der LinkedIn-Plattform – verarbeiten wir die von dir angegebenen Daten, um dich zu einer möglichen Zusammenarbeit zu kontaktieren, auch per E-Mail, Telefon oder über LinkedIn.",
          },
          {
            type: "p",
            text: "Rechtsgrundlage: Wir kontaktieren dich zu diesen Werbezwecken ausschließlich auf Grundlage deiner ausdrücklichen Einwilligung nach Art. 6 Abs. 1 lit. a DSGVO, die du aktiv über die Checkbox in unseren Formularen oder durch das Absenden eines LinkedIn Lead Gen Forms erteilst.",
          },
          { type: "h3", text: "LinkedIn Lead Gen Forms" },
          {
            type: "p",
            text: "Wenn du ein LinkedIn Lead Gen Form absendest, werden die in deinem LinkedIn-Profil hinterlegten Daten (z. B. Name, E-Mail und Unternehmen) von LinkedIn an uns übermittelt, damit wir uns bei dir melden können. Für die Auslieferung der Werbung und den Betrieb des Formulars ist die LinkedIn Ireland Unlimited Company eigenständig verantwortlich; beachte hierzu auch die Datenschutzerklärung von LinkedIn. Ab dem Zeitpunkt, in dem deine Daten bei uns eingehen, sind wir für die in dieser Erklärung beschriebene Verarbeitung verantwortlich.",
          },
          {
            type: "p",
            text: "Wir dokumentieren deine Einwilligung (einschließlich des Zeitpunkts der Erteilung), um die Einhaltung der Vorgaben nachweisen zu können. Lead-Daten speichern wir nur so lange, wie es für die Anbahnung der möglichen Zusammenarbeit erforderlich ist; danach werden sie gelöscht, sofern kein Vertragsverhältnis zustande kommt oder gesetzliche Aufbewahrungsfristen gelten.",
          },
          { type: "h3", text: "Dein Widerrufsrecht" },
          {
            type: "p",
            text: `Du kannst deine Einwilligung in die Kontaktaufnahme zu Werbezwecken jederzeit mit Wirkung für die Zukunft und ohne Angabe von Gründen widerrufen, indem du eine E-Mail an ${EMAIL} sendest. Durch den Widerruf wird die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung nicht berührt.`,
          },
        ],
      },
      {
        heading: "7. Deine Rechte",
        blocks: [
          { type: "p", text: "Du hast folgende Rechte in Bezug auf deine personenbezogenen Daten:" },
          {
            type: "ul",
            items: [
              "Recht auf Auskunft über deine gespeicherten Daten",
              "Recht auf Berichtigung unrichtiger Daten",
              "Recht auf Löschung deiner Daten",
              "Recht auf Einschränkung der Verarbeitung",
              "Recht auf Datenübertragbarkeit",
              "Recht auf Widerspruch gegen die Verarbeitung",
              "Recht, eine Einwilligung jederzeit zu widerrufen",
              "Recht auf Beschwerde bei einer Aufsichtsbehörde (z. B. dem Büro des Kommissars für den Schutz personenbezogener Daten in Zypern)",
            ],
          },
        ],
      },
      {
        heading: "8. Datensicherheit",
        blocks: [
          {
            type: "p",
            text: "Wir setzen branchenübliche Sicherheitsmaßnahmen ein, um deine personenbezogenen Daten vor unbefugtem Zugriff, Veränderung, Offenlegung oder Zerstörung zu schützen. Unsere Website nutzt eine SSL/TLS-Verschlüsselung für die sichere Datenübertragung.",
          },
        ],
      },
      {
        heading: "9. Änderungen dieser Erklärung",
        blocks: [
          {
            type: "p",
            text: "Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um Änderungen unserer Praktiken oder geltender Gesetze abzubilden. Die jeweils aktuelle Version ist stets auf unserer Website verfügbar.",
          },
        ],
      },
    ],
  },
  el: {
    seoTitle: "Πολιτική Απορρήτου | DevCore Websystems Ltd.",
    seoDesc:
      "Πώς η DevCore Websystems Ltd. διαχειρίζεται τα προσωπικά σου δεδομένα – συμπεριλαμβανομένων φορμών επικοινωνίας, διαφήμισης, lead generation και LinkedIn Lead Gen Forms – σύμφωνα με τον GDPR.",
    backToHome: "Επιστροφή στην αρχική",
    title: "Πολιτική Απορρήτου",
    lastUpdated: "Τελευταία ενημέρωση",
    breadcrumbHome: "Αρχική",
    breadcrumbSelf: "Πολιτική Απορρήτου",
    controllerHeading: "2. Υπεύθυνος επεξεργασίας",
    emailLabel: "Email",
    rightsContact: "Για να ασκήσεις οποιοδήποτε από αυτά τα δικαιώματα, επικοινώνησε μαζί μας στο",
    sections: [
      {
        heading: "1. Επισκόπηση",
        blocks: [
          {
            type: "p",
            text: "Η DevCore Websystems Ltd. («εμείς») λαμβάνει πολύ σοβαρά την προστασία των προσωπικών σου δεδομένων. Αυτή η πολιτική απορρήτου εξηγεί ποια δεδομένα συλλέγουμε όταν επισκέπτεσαι τον ιστότοπό μας ή επικοινωνείς μαζί μας, πώς τα χρησιμοποιούμε και ποια δικαιώματα έχεις σύμφωνα με τον Γενικό Κανονισμό για την Προστασία Δεδομένων της ΕΕ (GDPR).",
          },
        ],
      },
      {
        heading: "3. Συλλογή δεδομένων στον ιστότοπό μας",
        blocks: [
          { type: "h3", text: "Αρχεία καταγραφής διακομιστή" },
          {
            type: "p",
            text: "Ο πάροχος φιλοξενίας μας συλλέγει και αποθηκεύει αυτόματα πληροφορίες σε αρχεία καταγραφής που στέλνει ο browser σου: τύπος και έκδοση browser, λειτουργικό σύστημα, referrer URL, hostname του υπολογιστή πρόσβασης και ώρα του αιτήματος. Τα δεδομένα αυτά δεν μπορούν να αποδοθούν σε συγκεκριμένα άτομα και δεν συνδυάζονται με άλλες πηγές. Νομική βάση είναι το έννομο συμφέρον μας για την ασφαλή και σταθερή λειτουργία του ιστότοπου (άρθρο 6 παρ. 1 στ. στ’ GDPR).",
          },
          { type: "h3", text: "Φόρμες επικοινωνίας & κρατήσεων" },
          {
            type: "p",
            text: "Αν επικοινωνήσεις μαζί μας μέσω της φόρμας επικοινωνίας ή της φόρμας κράτησης, αποθηκεύουμε τα δεδομένα που παρέχεις (π.χ. όνομα, email, το μήνυμά σου και τις λεπτομέρειες του ραντεβού) για τη διεκπεραίωση του αιτήματος και τυχόν ερωτήσεις. Νομική βάση είναι η εκτέλεση προσυμβατικών ενεργειών και το έννομο συμφέρον μας να απαντήσουμε (άρθρο 6 παρ. 1 στ. β’ και στ’ GDPR)· εφόσον η φόρμα χρησιμοποιείται και για επικοινωνία με σκοπό τη διαφήμιση, η επεξεργασία βασίζεται στη συναίνεσή σου (άρθρο 6 παρ. 1 στ. α’ GDPR), βλ. ενότητα 6.",
          },
        ],
      },
      {
        heading: "4. Cookies",
        blocks: [
          {
            type: "p",
            text: "Ο ιστότοπός μας χρησιμοποιεί cookies — μικρά αρχεία κειμένου που αποθηκεύει ο browser σου και δεν προκαλούν βλάβη. Τα απολύτως απαραίτητα cookies απαιτούνται για τη λειτουργία του ιστότοπου και βασίζονται στο έννομο συμφέρον μας (άρθρο 6 παρ. 1 στ. στ’ GDPR). Τα cookies ανάλυσης και marketing τοποθετούνται μόνο αφού δώσεις τη συναίνεσή σου μέσω του banner cookies (άρθρο 6 παρ. 1 στ. α’ GDPR). Μπορείς να αλλάξεις ή να ανακαλέσεις την επιλογή σου ανά πάσα στιγμή από τις ρυθμίσεις cookies.",
          },
        ],
      },
      {
        heading: "5. Ανάλυση & τεχνολογίες διαφήμισης",
        blocks: [
          {
            type: "p",
            text: "Με την επιφύλαξη της συναίνεσής σου, χρησιμοποιούμε υπηρεσίες ανάλυσης και διαφήμισης όπως Google, Meta (Facebook/Instagram) και LinkedIn. Οι υπηρεσίες αυτές ενδέχεται να τοποθετούν cookies ή παρόμοιες τεχνολογίες και να επεξεργάζονται πληροφορίες για τη χρήση του ιστότοπου (π.χ. διεύθυνση IP, τύπο browser, σελίδες, conversions), ώστε να κατανοούμε το κοινό μας και να μετράμε την απόδοση των διαφημίσεών μας. Δεδομένα ενδέχεται να διαβιβαστούν σε τρίτες χώρες (π.χ. ΗΠΑ) βάσει των Τυποποιημένων Συμβατικών Ρητρών της ΕΕ. Νομική βάση είναι η συναίνεσή σου (άρθρο 6 παρ. 1 στ. α’ GDPR), την οποία μπορείς να ανακαλέσεις οποτεδήποτε.",
          },
        ],
      },
      {
        heading: "6. Άμεση διαφήμιση, Lead Generation & LinkedIn Lead Gen",
        blocks: [
          {
            type: "p",
            text: "Διεξάγουμε καμπάνιες διαφήμισης και lead generation (π.χ. μέσω Google, Meta και LinkedIn, συμπεριλαμβανομένων των LinkedIn Lead Gen Forms) για την προσέλκυση νέων πελατών. Αν υποβάλεις μία από τις φόρμες lead, επικοινωνίας ή κράτησης — ή ένα LinkedIn Lead Gen Form εντός της πλατφόρμας LinkedIn — επεξεργαζόμαστε τα στοιχεία που παρέχεις προκειμένου να επικοινωνήσουμε μαζί σου για μια πιθανή συνεργασία, μεταξύ άλλων μέσω email, τηλεφώνου ή LinkedIn.",
          },
          {
            type: "p",
            text: "Νομική βάση: Επικοινωνούμε μαζί σου για αυτούς τους διαφημιστικούς σκοπούς αποκλειστικά βάσει της ρητής σου συναίνεσης κατά το άρθρο 6 παρ. 1 στ. α’ GDPR, την οποία δίνεις ενεργά μέσω του checkbox στις φόρμες μας ή με την υποβολή ενός LinkedIn Lead Gen Form.",
          },
          { type: "h3", text: "LinkedIn Lead Gen Forms" },
          {
            type: "p",
            text: "Όταν υποβάλεις ένα LinkedIn Lead Gen Form, τα δεδομένα που είναι αποθηκευμένα στο προφίλ σου στο LinkedIn (όπως όνομα, email και εταιρεία) διαβιβάζονται από το LinkedIn σε εμάς ώστε να επικοινωνήσουμε. Για την προβολή της διαφήμισης και τη λειτουργία της φόρμας, η LinkedIn Ireland Unlimited Company ενεργεί ως ανεξάρτητος υπεύθυνος επεξεργασίας· ανέτρεξε και στην πολιτική απορρήτου του LinkedIn. Από τη στιγμή που τα δεδομένα σου φτάνουν σε εμάς, είμαστε εμείς ο υπεύθυνος επεξεργασίας για όσα περιγράφονται εδώ.",
          },
          {
            type: "p",
            text: "Τεκμηριώνουμε τη συναίνεσή σου (συμπεριλαμβανομένης της ώρας που δόθηκε) για να μπορούμε να αποδείξουμε τη συμμόρφωσή μας. Διατηρούμε τα δεδομένα lead μόνο για όσο διάστημα είναι απαραίτητο για την επιδίωξη της πιθανής συνεργασίας· στη συνέχεια διαγράφονται, εκτός αν συναφθεί συμβατική σχέση ή ισχύουν νόμιμες περίοδοι διατήρησης.",
          },
          { type: "h3", text: "Δικαίωμα ανάκλησης" },
          {
            type: "p",
            text: `Μπορείς να ανακαλέσεις τη συναίνεσή σου για επικοινωνία με σκοπό τη διαφήμιση ανά πάσα στιγμή, με ισχύ για το μέλλον και χωρίς αιτιολόγηση, στέλνοντας email στο ${EMAIL}. Η ανάκληση δεν θίγει τη νομιμότητα της επεξεργασίας που πραγματοποιήθηκε πριν από αυτήν.`,
          },
        ],
      },
      {
        heading: "7. Τα δικαιώματά σου",
        blocks: [
          { type: "p", text: "Έχεις τα ακόλουθα δικαιώματα σχετικά με τα προσωπικά σου δεδομένα:" },
          {
            type: "ul",
            items: [
              "Δικαίωμα πρόσβασης στα αποθηκευμένα δεδομένα σου",
              "Δικαίωμα διόρθωσης ανακριβών δεδομένων",
              "Δικαίωμα διαγραφής των δεδομένων σου",
              "Δικαίωμα περιορισμού της επεξεργασίας",
              "Δικαίωμα φορητότητας δεδομένων",
              "Δικαίωμα εναντίωσης στην επεξεργασία",
              "Δικαίωμα ανάκλησης της συναίνεσης ανά πάσα στιγμή",
              "Δικαίωμα υποβολής καταγγελίας σε εποπτική αρχή (π.χ. στο Γραφείο Επιτρόπου Προστασίας Δεδομένων Προσωπικού Χαρακτήρα Κύπρου)",
            ],
          },
        ],
      },
      {
        heading: "8. Ασφάλεια δεδομένων",
        blocks: [
          {
            type: "p",
            text: "Χρησιμοποιούμε μέτρα ασφαλείας του κλάδου για την προστασία των προσωπικών σου δεδομένων από μη εξουσιοδοτημένη πρόσβαση, αλλοίωση, αποκάλυψη ή καταστροφή. Ο ιστότοπός μας χρησιμοποιεί κρυπτογράφηση SSL/TLS για την ασφαλή μετάδοση δεδομένων.",
          },
        ],
      },
      {
        heading: "9. Αλλαγές σε αυτή την πολιτική",
        blocks: [
          {
            type: "p",
            text: "Διατηρούμε το δικαίωμα να ενημερώνουμε αυτή την πολιτική απορρήτου ώστε να αντικατοπτρίζει αλλαγές στις πρακτικές μας ή στην ισχύουσα νομοθεσία. Η τρέχουσα έκδοση είναι πάντα διαθέσιμη στον ιστότοπό μας.",
          },
        ],
      },
    ],
  },
};

const PrivacyPolicy = () => {
  const ref = useScrollReveal();
  const { lang, localize } = useLocale();
  const c = CONTENT[lang] ?? CONTENT.en;
  const langPath = lang === "en" ? "" : `/${lang}`;
  const canonical = `https://devcore.systems${__lp("/privacy-policy", lang)}`;

  const renderBlock = (block: Block, i: number) => {
    if (block.type === "h3") {
      return (
        <h3 key={i} className="text-lg font-semibold text-foreground mb-2 mt-4">
          {block.text}
        </h3>
      );
    }
    if (block.type === "ul") {
      return (
        <ul key={i} className="list-disc list-inside space-y-1.5">
          {block.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mb-3 last:mb-0">
        {block.text}
      </p>
    );
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title={c.seoTitle}
        description={c.seoDesc}
        canonical={canonical}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          inLanguage: LANG_HREFLANG[lang],
          itemListElement: [
            { "@type": "ListItem", position: 1, name: c.breadcrumbHome, item: `https://devcore.systems${langPath}/` },
            { "@type": "ListItem", position: 2, name: c.breadcrumbSelf, item: canonical },
          ],
        }}
      />

      <Navbar />
      <main className="pt-28 pb-24 md:pt-36 md:pb-32">
        <div ref={ref} className="container section-reveal max-w-3xl">
          <Link
            to={localize("/")}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            {c.backToHome}
          </Link>

          <h1 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-12">
            {c.title}
          </h1>

          <div className="space-y-10 text-muted-foreground leading-relaxed">
            {/* Section 1 (overview) */}
            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{c.sections[0].heading}</h2>
              {c.sections[0].blocks.map(renderBlock)}
            </section>

            {/* Controller */}
            <section>
              <h2 className="text-xl font-bold text-foreground mb-3">{c.controllerHeading}</h2>
              <div className="space-y-1">
                {ADDRESS.map((line, i) => (
                  <p key={i} className={i === 0 ? "font-medium text-foreground" : undefined}>
                    {line}
                  </p>
                ))}
                <p>
                  {c.emailLabel}:{" "}
                  <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
                    {EMAIL}
                  </a>
                </p>
              </div>
            </section>

            {/* Remaining numbered sections (from section 3 onwards) */}
            {c.sections.slice(1).map((section, i) => (
              <section key={i}>
                <h2 className="text-xl font-bold text-foreground mb-3">{section.heading}</h2>
                {section.blocks.map(renderBlock)}
                {section.heading.startsWith("7.") && (
                  <p className="mt-3">
                    {c.rightsContact}{" "}
                    <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">
                      {EMAIL}
                    </a>
                    .
                  </p>
                )}
              </section>
            ))}

            <section className="pt-6 border-t border-border">
              <p className="text-sm">
                © {new Date().getFullYear()} DevCore Websystems Ltd. All rights reserved.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
